import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../context/UserContext';
import { FaTimes, FaChevronDown, FaChevronUp, FaDownload } from 'react-icons/fa';
import axios from 'axios';

const VoteCard = ({ onClose }) => {
  const { phoneNo, state, electoralDivision } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState('federal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htvData, setHtvData] = useState({ house: [], senate: [] });
  const [expandedSections, setExpandedSections] = useState(new Set(['House of Representatives', 'Senate']));
  const [activeDownloadSection, setActiveDownloadSection] = useState(null);

  console.log('UserContext values:', { phoneNo, state, electoralDivision }); // Debug log

  const fetchHtvData = async () => {
    if (!phoneNo) {
      setError('Phone number is required');
      return;
    }

    if (activeTab === 'federal') {
      if (!electoralDivision) {
        setError('Electoral division is required for House of Representatives');
        return;
      }
      if (!state) {
        setError('State is required for Senate');
        return;
      }
    } else {
      if (!state) {
        setError('State is required for state parliament');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Debug log before API call
      console.log('Making API call with params:', {
        phoneNo,
        type: activeTab.toUpperCase(),
        electoralDivision: activeTab === 'federal' ? electoralDivision : state,
        state
      });

      // For both federal and state, make a single API call
      // For federal, we'll use state as the constituency for Senate members
      const response = await axios.get('/api/htv-card-aec', {
        params: {
          phoneNo,
          type: activeTab.toUpperCase(), // Ensure consistent case
          electoralDivision: activeTab === 'federal' ? electoralDivision : state,
          state: state // Add state parameter for Senate filtering
        }
      });

      // Debug log after API call
      console.log('API Response:', response.data);

      // No need to filter results anymore since backend handles it
      setHtvData({
        house: response.data.house || [],
        senate: response.data.senate || []
      });

      console.log(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Parliament Data:`, {
        params: {
          type: activeTab.toUpperCase(),
          state,
          electoralDivision,
          response: response.data
        }
      });
    } catch (err) {
      console.error('API Error:', err.response || err);
      setError(err.response?.data?.message || 'Failed to fetch HTV card data');
      setHtvData({ house: [], senate: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHtvData();
  }, [activeTab, phoneNo, electoralDivision, state]);

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleDownload = async (section) => {
    try {
      console.log(`Downloading ${section} as PDF`);
      
      if (!phoneNo || !electoralDivision || !state) {
        alert('Missing required information. Please ensure all details are available.');
        return;
      }

      const response = await axios({
        method: 'get',
        url: '/api/download-htv',
        params: {
          phoneNo,
          type: activeTab.toUpperCase(),
          electoralDivision: activeTab === 'federal' ? electoralDivision : state,
          state,
          section,
          format: 'pdf'
        },
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HTV_${section.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading vote card:', error);
      alert('Failed to download. Please try again.');
    }
  };

  const renderAccordion = (title, politicians) => (
    <div style={{ marginBottom: "15px" }}>
      <div 
        onClick={() => toggleSection(title)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <h4 style={{ margin: 0, display: "flex", alignItems: "center" }}>
            {title}
            <FaDownload 
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(title);
              }}
              style={{ 
                cursor: "pointer", 
                marginLeft: "10px",
                color: "#007bff",
                fontSize: "1.2rem"
              }} 
            />
          </h4>
        </div>
        <div style={{ marginLeft: "auto" }}>
          {expandedSections.has(title) ? <FaChevronUp color="red" /> : <FaChevronDown color="red" />}
        </div>
      </div>

      {expandedSections.has(title) && (
        <div style={{ marginTop: "10px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                <th style={{ padding: "10px", textAlign: "center" }}>Rank</th>
                <th style={{ padding: "10px", textAlign: "left" }}>Candidate Name</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {politicians && politicians.length > 0 ? (
                [...politicians]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((politician) => (
                    <tr key={politician.name} style={{ borderBottom: "1px solid #dee2e6" }}>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {politician.rank === 'unranked' ? (
                          <div 
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#087cfc",
                              borderRadius: "50%",
                              margin: "auto",
                              cursor: "help"
                            }}
                            title="Unranked Candidate"
                          ></div>
                        ) : politician.rank}
                      </td>
                      <td style={{ padding: "10px" }}>{politician.name}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{politician.percentage}%</td>
                    </tr>
                  ))
              ) : (
                <tr style={{ borderBottom: "1px solid #dee2e6" }}>
                  <td colSpan="3" style={{ padding: "10px", textAlign: "center" }}>No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
        zIndex: 10000,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ margin: 0 }}>
            Personalised Vote Card - {activeTab === 'federal' ? 'Federal' : 'State'} Parliament
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setActiveTab('federal')}
                style={{
                  padding: '5px 10px',
                  backgroundColor: activeTab === 'federal' ? '#007bff' : '#f8f9fa',
                  color: activeTab === 'federal' ? 'white' : 'black',
                  border: '1px solid #dee2e6',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Federal
              </button>
              {/* <button
                onClick={() => setActiveTab('state')}
                style={{
                  padding: '5px 10px',
                  backgroundColor: activeTab === 'state' ? '#007bff' : '#f8f9fa',
                  color: activeTab === 'state' ? 'white' : 'black',
                  border: '1px solid #dee2e6',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                State
              </button> */}
            </div>
            <FaTimes onClick={onClose} style={{ cursor: "pointer", fontSize: "1.5rem" }} />
          </div>
        </div>

        {error && (
          <div style={{
            padding: "10px",
            marginBottom: "15px",
            backgroundColor: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "5px",
            color: "#721c24"
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading vote card data...
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {renderAccordion("House of Representatives", htvData.house)}
            {renderAccordion("Senate", htvData.senate)}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoteCard;
